import React, { useMemo } from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
  Selection,
} from '@syncfusion/ej2-react-grids';

import InsuranceEditDialog from '../../dialogs/InsuranceEditDialog';
import { useAppSelector } from '../../../../../app/store';
import { useInsuranceActions, useInsuranceEditDialog } from './hooks';
import { ActionsTemplate } from './templates';

// Grid page settings
const pageSettings = { pageSize: 5 };

export interface InsurancesGridProps {
  /** Optional ID for testing */
  testId?: string;
}

/**
 * InsurancesGrid - Displays encounter insurances with priority ordering
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for actions and dialog state
 * - Clean component structure
 */
export const InsurancesGrid: React.FC<InsurancesGridProps> = ({ testId }) => {
  // Get encounter from Redux store
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const insurances = encounter?.insurances || [];

  // Derive sorted insurances and cancelled state locally
  const sortedInsurances = [...insurances].sort((a, b) => (a.priority || 0) - (b.priority || 0));
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Use custom hooks for actions and dialog state
  const { handleMoveUp, handleMoveDown } = useInsuranceActions();
  const { editDialogOpen, selectedInsurance, handleRowSelection, handleClose } =
    useInsuranceEditDialog(sortedInsurances, isEncounterCancelled);

  // Create actions template with row context
  const actionsTemplate = useMemo(
    () => (props: any) => {
      const index = sortedInsurances.findIndex((ins) => ins.id === props.id);
      const isFirst = index === 0;
      const isLast = index === sortedInsurances.length - 1;

      return (
        <ActionsTemplate
          insuranceId={props.id}
          isFirst={isFirst}
          isLast={isLast}
          isDisabled={isEncounterCancelled}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
        />
      );
    },
    [sortedInsurances, isEncounterCancelled, handleMoveUp, handleMoveDown]
  );

  return (
    <>
      <GridComponent
        dataSource={sortedInsurances}
        allowPaging={true}
        allowSorting={true}
        pageSettings={pageSettings}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="priority"
            headerText="Priority"
            width="80"
            textAlign="Center"
          />
          <ColumnDirective
            field="payerName"
            headerText="Payer"
            width="200"
          />
          <ColumnDirective
            field="planName"
            headerText="Plan"
            width="150"
          />
          <ColumnDirective
            field="groupNumber"
            headerText="Group #"
            width="120"
          />
          <ColumnDirective
            field="memberId"
            headerText="Member ID"
            width="120"
          />
          <ColumnDirective
            headerText="Actions"
            width="100"
            textAlign="Center"
            template={actionsTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort, Selection]} />
      </GridComponent>

      {selectedInsurance && (
        <InsuranceEditDialog
          open={editDialogOpen}
          onClose={handleClose}
          insurance={selectedInsurance}
        />
      )}
    </>
  );
};

export default InsurancesGrid;
